import React,{ useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    Container,
    Divider,
    Fade,
    Avatar,
    Paper
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import PsychologyIcon from '@mui/icons-material/Psychology';
import useParallaxEffect from "../common/useParallaxEffect";
import ScrollToTop from "../common/ScrollToTop";

const StyledAccordion = styled(Accordion)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    boxShadow: 'none',
    '&:before': {
        display: 'none',
    },
    '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    transition: 'background-color 0.3s ease',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const StyledAccordionSummary = styled(AccordionSummary)(() => ({
    '& .MuiAccordionSummary-expandIconWrapper': {
        transition: 'transform 0.3s',
    },
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(180deg)',
    },
}));

const QuestionTypography = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    '& .MuiSvgIcon-root': {
        marginRight: theme.spacing(1),
    },
}));
const About = () => {
   useParallaxEffect();
   useEffect(() => {
        window.scrollTo(0, 0);
        }, []);
    
    const [expanded, setExpanded] = React.useState(false);
    const ContentPaper = styled(Paper)(({ theme }) => ({
        padding: theme.spacing(3),
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: theme.shadows[4],
        },
    }));

    const IconWrapper = styled(Box)(({ theme }) => ({
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderRadius: '50%',
        padding: theme.spacing(2),
        display: 'inline-flex',
        marginBottom: theme.spacing(2),
    }));
    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };
    const features = [
        {
            title: "BCBA Owned",
            description: "Pivotal is owned and operated by respected BCBAs with over a decade of experience in the field.",
            icon: <EmojiEventsIcon fontSize="large" />,
            iconColor: '#FFD700' // Gold color
        },
        {
            title: "Dedicated Staff",
            description: "We have a team of professional and certified BCBAs, RBTs and BTs with vast experience in the field of ABA.",
            icon: <PersonIcon fontSize="large" />,
            iconColor: '#4169E1' // Royal Blue
        },
        {
            title: "Cultural Competency",
            description: "Everyone of our staff is equipped and trained in cultural competency and sensitivity to help navigate the diverse population that we serve.",
            icon: <PublicIcon fontSize="large" />,
            iconColor: '#2E8B57' // Sea Green
        }
    ];
    const faqData = [
        {
            question: "Will ABA help my child?",
            answer: "Therapy guided by the principles of ABA will address the developmental deficiencies identified during an initial assessment of your child's functioning across dozens of areas. This often includes areas of verbal communication, nonverbal communication, social skills, play skills, toilet training, self-care skills, and day-to-day living skills."
        },
        {
            question: "When Should My Child Begin Services?",
            answer: "The earlier, the better. Decades of clinical research have demonstrated that children who receive ABA at an early age (even starting at 2 years old!) make the most significant improvements in their areas of deficit. However, it is never too late to begin."
        },
        {
            question: "My Child Is Getting Speech and Occupational Therapy. Is That Enough?",
            answer: "We view these providers as valuable complements to the intensive, evidence-based services we provide. Many speech or occupational therapy programs provide an hour or two of therapy per week as opposed to up to 40 hours per week with an intensive ABA program. We wish to partner with them in order to provide a comprehensive, well-rounded treatment plan."
        },
        {
            question: "Will My Child Need an Autism Diagnosis to get Treatment?",
            answer: "Children are not required to have a diagnosis for treatment, but do need a formal diagnosis to receive coverage from insurance providers or waivers."
        },
        {
            question: "How Long are Therapy Sessions?",
            answer: "Therapy sessions can take place in the home, community, or school setting, depending on what is appropriate for the person receiving treatment.Typically, sessions are scheduled for 2-4 hours in length, dependent on several factors."
        },
        {
            question: "What Does the Research on ABA Show?",
            answer: "Over the past 40 years, hundreds of peer-reviewed studies have shown extremely positive results for comprehensive, individualized, intensive early intervention ABA programs for children with autism."
        },
        {
            question: "Will my insurance cover ABA?",
            answer: "Each insurance plan is different. Our staff is happy to navigate the insurance process with you to determine your benefits and eligibility."
        },
        {
            question: "Who Will Be Providing the Services?",
            answer: "Our services are provided by a team of highly qualified professionals:\n\n" +
                "• Board Certified Behavior Analysts (BCBA): BCBAs are certified by the BACB and licensed by the state they work and are equipped to provide specialized ABA therapy. The BCBA assigned to your child's case oversees the entire treatment plan and supervises the Behavior Technician(BT) or Registered Behavior Technician(RBT).\n\n" +
                "• Board Certified Assistant Behavior Analysts (BCaBA): These professionals are certified by the BACB and work under the supervision of a certified BCBA. They contribute valuable support to the therapy process, and can work directly with your child or supervise the technicians working with your child. \n\n" +
                "• Registered Behavior Technicians (RBT): RBTs are registered with the BACB and operate under the guidance of a certified BCBA, playing a crucial role in the delivery of ABA services.They work directly with your child to deliver ABA therapy and have taken additional coursework. They have also passed a comprehensive exam to receive their certification from the BACB.\n\n" +
                "• Behavior Technicians (BT): Similar to RBTs, BTs provide ABA services under the supervision of a certified BCBA, but they are not registered with the BACB. In order to ensure consistent and high-quality care, Pivotal works with each BT to provide the additional coursework, training and supervision needed for them to take their comprehensive exam."
        }
    ];

    return (
        <>
            <Container maxWidth="lg">
                <div id="back-to-top-anchor"/>
                {/* Hero Section */}
                <Fade in={true} timeout={1000}>
                        <Box sx={{
                            bgcolor: '#f5f5f5',
                            pt: '64px',  // padding-top: 64px
                            pb: '30px',  // padding-bottom: 30px
                            borderBottom: '1px solid #e0e0e0'
                        }}>
                            <Typography
                                variant="h3"
                                component="h3"
                                align="center"
                                sx={{
                                    color: 'textSecondary',
                                    fontWeight: 500
                                }}
                            >
                                Your Child’s Well-Being Means Everything to Us...
                            </Typography>
                        </Box>
                    </Fade>
                
                {/* About Content */}
                <Fade in={true} timeout={1500}>
                    <Box sx={{mb: 6, mt: 4}}>
                        <Grid container justifyContent="center">
                            <Grid item xs={12} md={10}>
                                <ContentPaper elevation={2}>
                                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                        <IconWrapper>
                                            <PsychologyIcon fontSize="large"/>
                                        </IconWrapper>
                                        <Typography variant="h4" gutterBottom fontWeight="bold" color="primary"
                                                    align="center">
                                            Pivotal Behavioral Services LLC
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }} align="center">
                                            Is a premier provider of Applied Behavior Analysis (ABA) therapy. We specialize
                                            in delivering personalized, evidence-based interventions to individuals
                                            diagnosed with Autism Spectrum Disorder (ASD) and other developmental
                                            disabilities.
                                        </Typography>
                                        <Divider sx={{width: '50%', my: 3}}/>
                                        <Typography variant="body1" sx={{ mb: 2 }} align="center">
                                            Our team provides compassionate, evidence-based therapy and focuses on helping
                                            individuals develop essential life skills, improve social interactions, and
                                            achieve greater independence.
                                        </Typography>
                                    </Box>
                                </ContentPaper>
                            </Grid>
                        </Grid>
                    </Box>
                </Fade>
    
                {/* Features Section */}
                <Fade in={true} timeout={2000}>
                    <Box sx={{
                        width: '100%',
                        mx: 'auto',
                        mb: 6,
                        display: 'flex',
                        flexDirection: {xs: 'column', sm: 'row'},
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2
                    }}>
                        {features.map((feature, index) => (
                            <Box key={index} sx={{
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <Card sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    p: 2,
                                    boxShadow: 3,
                                    '&:hover': {
                                        boxShadow: 6,
                                        transform: 'translateY(-5px)',
                                        transition: 'all 0.3s ease-in-out'
                                    }
                                }}>
                                    <Avatar sx={{
                                        width: 60,
                                        height: 60,
                                        bgcolor: feature.iconColor,
                                        mb: 1,
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 30,
                                            color: '#fff'
                                        }
                                    }}>
                                        {feature.icon}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                                            {feature.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{fontSize: '0.8rem'}}>
                                            {feature.description}
                                        </Typography>
                                    </Box>
                                </Card>
                            </Box>
                        ))}
                    </Box>
                </Fade>
    
                {/* FAQ Section */}
                <Fade in={true} timeout={2500}>
                    <Box sx={{mb: 6}}>
                        <Typography variant="h4" component="h2" gutterBottom align="center"
                                    sx={{mb: 4, position: 'relative'}}>
                            Frequently Asked Questions
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: '-10px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: '80px',
                                    height: '4px',
                                    backgroundColor: 'primary.main',
                                    borderRadius: '2px',
                                }}
                            />
                        </Typography>
                        {faqData.map((faq, index) => (
                            <StyledAccordion
                                key={index}
                                expanded={expanded === `panel${index}`}
                                onChange={handleChange(`panel${index}`)}
                            >
                                <StyledAccordionSummary
                                    expandIcon={<ExpandMoreIcon/>}
                                    aria-controls={`panel${index}a-content`}
                                    id={`panel${index}a-header`}
                                >
                                    <QuestionTypography>
                                        <QuestionAnswerIcon fontSize="small"/>
                                        {faq.question}
                                    </QuestionTypography>
                                </StyledAccordionSummary>
                                <AccordionDetails>
                                    <Typography component="div" sx={{ pl: 4 }}>
                                        {faq.answer.split('\n\n').map((paragraph, index) => (
                                            paragraph.startsWith('•') ? (
                                                <ul key={index} style={{ paddingLeft: '20px', marginTop: '10px', marginBottom: '10px' }}>
                                                    {paragraph.split('\n').map((item, itemIndex) => (
                                                        <li key={itemIndex}>
                                                            <Typography variant="body2">
                                                                {item.replace('•', '').trim()}
                                                            </Typography>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <Typography key={index} variant="body2" sx={{ mb: 2 }}>
                                                    {paragraph}
                                                </Typography>
                                            )
                                        ))}
                                    </Typography>
                                </AccordionDetails>
                            </StyledAccordion>
                        ))}
                    </Box>
                </Fade>
            </Container>
            <ScrollToTop />
        </>
    );
};

    export default About;